import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { LocaleContext } from "../state/LocaleContext.js";

const COOKIE_NAME = 'intro_tutorial_seen';
const TOTAL_SLIDES = 7;
const INTRO_ENABLED = true;
const DISCLAIMER_SLIDE_INDEX = 1; // 0-indexed, so slide 2

export default function IntroTutorial({ forceShow = false, onClose: onCloseProp } = {}) {
  const { tUI } = useContext(LocaleContext);
  const [show, setShow] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const dialogRef = useRef(null);
  const prevBtnRef = useRef(null);
  const nextBtnRef = useRef(null);
  const skipBtnRef = useRef(null);
  const checkboxRef = useRef(null);

  useEffect(() => {
    const cookies = document.cookie || '';
    const hasSeenTutorial = cookies
      .split('; ')
      .find(row => row.startsWith(`${COOKIE_NAME}=`));

    if ((forceShow || !hasSeenTutorial) && INTRO_ENABLED) {
      setShow(true);

      // Failsafe: auto-close after 5 minutes if still showing
      const failsafeTimeout = setTimeout(() => {
        setShow(false);
      }, 5 * 60 * 1000);

      return () => clearTimeout(failsafeTimeout);
    } else {
      setShow(false);
      setDisclaimerAccepted(true);
    }
  }, []);

  // Focus trap — keep focus inside the dialog
  const handleKeyDown = useCallback((e) => {
    if (!show) return;

    if (e.key === 'Escape' && disclaimerAccepted) {
      setShow(false);
      return;
    }

    if (e.key === 'Tab') {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }, [show, disclaimerAccepted]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Move focus into dialog on open, and to checkbox on disclaimer slide
  useEffect(() => {
    if (!show) return;
    requestAnimationFrame(() => {
      if (activeIndex === DISCLAIMER_SLIDE_INDEX && checkboxRef.current) {
        checkboxRef.current.focus();
      } else if (nextBtnRef.current && !nextBtnRef.current.disabled) {
        nextBtnRef.current.focus();
      } else if (prevBtnRef.current && !prevBtnRef.current.disabled) {
        prevBtnRef.current.focus();
      }
    });
  }, [show, activeIndex]);

  // Hide body scroll when overlay is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  const handleDisclaimerChange = (e) => {
    const checked = e.target.checked;
    setDisclaimerAccepted(checked);

    if (checked) {
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = `${COOKIE_NAME}=true; expires=${expires.toUTCString()}; path=/`;

      // Auto-advance to next slide after accepting disclaimer
      if (activeIndex === DISCLAIMER_SLIDE_INDEX && activeIndex < TOTAL_SLIDES - 1) {
        setTimeout(() => setActiveIndex(activeIndex + 1), 300);
      }
    } else {
      document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    }
  };

  const handleClose = () => {
    setShow(false);
    onCloseProp?.();
  };

  const handlePrev = () => {
    const targetIndex = activeIndex - 1;
    if (disclaimerAccepted && targetIndex === DISCLAIMER_SLIDE_INDEX) {
      setActiveIndex(Math.max(0, targetIndex - 1));
    } else {
      setActiveIndex(Math.max(0, targetIndex));
    }
  };

  const handleNext = () => {
    setActiveIndex(Math.min(TOTAL_SLIDES - 1, activeIndex + 1));
  };

  if (!show) return null;

  const isFirstSlide = activeIndex === 0;
  const isLastSlide = activeIndex === TOTAL_SLIDES - 1;
  const isDisclaimerSlide = activeIndex === DISCLAIMER_SLIDE_INDEX;
  const nextDisabled = isLastSlide;

  return (
    <>
      {/* Semi-transparent overlay */}
      <div
        onClick={() => {
          if (disclaimerAccepted) handleClose();
        }}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 10000,
        }}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Tutorial and disclaimer — slide ${activeIndex + 1} of ${TOTAL_SLIDES}`}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10000,
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#333',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Slide indicators */}
        <div
          role="tablist"
          aria-label="Tutorial slides"
          style={{
            padding: '12px 16px',
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            backgroundColor: '#222'
          }}
        >
          {[...Array(TOTAL_SLIDES)].map((_, index) => (
            <div
              key={index}
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={`Slide ${index + 1}`}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: index === activeIndex ? '#fff' : 'rgba(255, 255, 255, 0.4)',
                transition: 'background-color 0.3s'
              }}
            />
          ))}
        </div>

        {/* Slide content */}
        <div
          role="tabpanel"
          aria-label={`Slide ${activeIndex + 1} of ${TOTAL_SLIDES}`}
          aria-live="polite"
          style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#333' }}
        >
          <img
            src={`/assets/images/intro/start-screen-${activeIndex + 1}.png`}
            alt={`Tutorial slide ${activeIndex + 1} of ${TOTAL_SLIDES}`}
            style={{
              maxWidth: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              display: 'block'
            }}
          />

          {/* Disclaimer checkbox on slide 2 */}
          {isDisclaimerSlide && (
            <div
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                width: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                padding: '8px 16px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '36px'
              }}
            >
              <label
                htmlFor="disclaimerCheckbox"
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontSize: '12px',
                  lineHeight: '1.2',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textAlign: 'center'
                }}
              >
                <input
                  ref={checkboxRef}
                  className="form-check-input"
                  type="checkbox"
                  id="disclaimerCheckbox"
                  checked={disclaimerAccepted}
                  onChange={handleDisclaimerChange}
                  aria-required="true"
                  style={{ margin: 0, flexShrink: 0 }}
                />
                <span>{tUI("label_disclaimer_agree", "I have read and acknowledge the above.")}</span>
              </label>
            </div>
          )}
        </div>

        {/* Modal footer with navigation controls */}
        <div
          style={{
            padding: '16px 20px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#222',
            borderTop: '1px solid #444'
          }}
        >
          {/* Previous button */}
          <button
            ref={prevBtnRef}
            onClick={handlePrev}
            disabled={isFirstSlide}
            aria-label={`Previous slide${isFirstSlide ? ' (disabled, already on first slide)' : ''}`}
            style={{
              padding: '10px 24px',
              backgroundColor: isFirstSlide ? '#555' : 'var(--theme-primary)',
              color: isFirstSlide ? '#999' : '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: isFirstSlide ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              minWidth: '100px'
            }}
          >
            {tUI("label_previous", "Previous")}
          </button>

          {/* Skip/Exit button (hidden until disclaimer accepted) */}
          {disclaimerAccepted && (
            <button
              ref={skipBtnRef}
              onClick={handleClose}
              aria-label={isLastSlide ? 'Exit tutorial' : 'Skip remaining slides'}
              style={{
                padding: '10px 24px',
                backgroundColor: '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                minWidth: '100px'
              }}
            >
              {isLastSlide ? tUI("label_exit", "Exit") : tUI("label_skip", "Skip")}
            </button>
          )}

          {/* Next button */}
          <button
            ref={nextBtnRef}
            onClick={handleNext}
            disabled={nextDisabled}
            aria-label={
              isDisclaimerSlide && !disclaimerAccepted
                ? 'Next slide (accept disclaimer to continue)'
                : isLastSlide
                  ? 'Next slide (disabled, last slide)'
                  : 'Next slide'
            }
            style={{
              padding: '10px 24px',
              backgroundColor: nextDisabled ? '#555' : 'var(--theme-primary)',
              color: nextDisabled ? '#999' : '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: nextDisabled ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              minWidth: '100px'
            }}
          >
            {tUI("label_next", "Next")}
          </button>
        </div>
      </div>
    </>
  );
}
